import type { SupportingPagesUnifiedColumn } from './supporting-pages-unified-columns';
import type { DomainTopicUrlRow } from './topic-supporting-pages';
import {
  alreadyOnPageQuote,
  alreadyOnPageQuoteLine,
  newPageIdeaSubject,
  type AlreadyOnPageQuote,
  type OwnPagePassage
} from './already-on-page';

/**
 * The Supporting pages card, reduced to what a review decision needs.
 *
 * The deep-dive card answers several questions at once: which domains rank for
 * this URL's topics, what their strongest page is, what you already cover, and
 * what you don't. That is right for Inventory, where an operator is working out
 * what is going on.
 *
 * On the review surface the card answers exactly one question — **what closely
 * related pages do they have that I don't?** — because the only thing being
 * decided is whether to queue writing them. Everything that is not that answer
 * is furniture there, and each piece of furniture is a thing the operator has
 * to read past before they can press a key.
 *
 * So three things are dropped, and each was asked for by name:
 *
 * **Your own site's column.** It answers a different question — *do I have a
 * supporting cluster?* — which is worth asking and is not this card's question.
 *
 * **Their top page.** It is the domain's strongest page on the topic, which
 * establishes why the domain is worth listening to. Useful when you are
 * orienting; noise when you already are.
 *
 * **Rows you already cover.** If the card is a list of pages to write, a page
 * you have written is not on it.
 *
 * And a gap row is dropped when it is not a page worth writing. These are the
 * agent export's guards (AGENT_EXPORT_MCP.md §2a), which used to run only in
 * the export; the review card listed the same ideas unguarded, so a page we
 * already had and the "~0/mo" photographer pages still asked to be written on
 * the belt (TECHNICAL_DEBT §0ce). The export reads this card, so one rule now
 * decides both: see `newPageIdeaBlocked`.
 */

/**
 * A rival page must match this page's topic at least this well to be shown.
 *
 * Show an empty competitor column rather than that domain’s best row when even
 * its best row is this weak against the focal page (findings §15 / FW-URL-05
 * follow-up). `SUPPORTING_PAGE_FOCAL_MATCH_REL_BAND` is relative, so a domain
 * whose whole pool is off-topic still keeps a “top” row — dokie
 * `/blog/reviews/pitch-review` surfaced at 0.069 and 0.074 as a peer for PDF
 * tools. An empty column is the honest answer.
 *
 * The review card also applies it to each new-page idea (AGENT_EXPORT_MCP §3.1's
 * gate): on the 5 Oct review every idea graded right matched at 0.19 or more;
 * the white-label page's off-topic ideas matched at 0.06–0.08.
 *
 * Defined here because the review rules live in `shared/`; the backend's
 * `supporting-page-focal-match.ts` re-exports it for the column filter.
 */
export const SUPPORTING_PAGE_COMPETITOR_COLUMN_MIN_MATCH = 0.12;

/**
 * A new-page idea needs a rival page the card shows as at least "~1/mo".
 *
 * The card rounds the rival page's estimated visits to the nearest whole
 * number, so this is the line between a figure it prints as "~0/mo" and one it
 * does not. On the 5 Oct review the four "~0/mo" photography pages under
 * /blog/honeybook-alternatives were graded wrong, and every new page graded
 * right carried at least ~13/mo; on the 2026-10-15 baseline the three good
 * ideas carry ~13, ~114 and ~1,115/mo, the four photographer pages 0–0.5. It is
 * a floor on evidence of demand, not a ranking: the rival's traffic is not
 * search volume, and nothing below it is claimed to be worthless — only
 * unproven. Unknown traffic counts as none.
 */
export const NEW_PAGE_MIN_RIVAL_VISITS = 1;

/** The last non-empty path segment, lowercased: `/blog/basecamp-alternatives/` → `basecamp-alternatives`. */
export function finalPathSegment(url: string): string {
  let path = url;
  try {
    path = new URL(url).pathname || '/';
  } catch {
    // Not absolute: read it as a path.
  }
  const segments = path
    .split('/')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return segments.length ? segments[segments.length - 1] : '';
}

/** Why a rival page is not a page to write, or null when it is one. */
export type NewPageIdeaBlock = 'own_page_exists' | 'off_topic' | 'no_demand';

/**
 * Whether a rival page the card would list is not worth asking anyone to write.
 *
 * - `own_page_exists`: our site already has a URL ending in the same path
 *   segment (§0am: `basecamp-alternatives` listed as missing while we had it,
 *   and `honeybook-alternatives` suggested on /blog/honeybook-alternatives
 *   itself). `similarOnYourSite` misses these; this does not fix §0am, it
 *   stops the card asking for a page that exists.
 * - `off_topic`: below `SUPPORTING_PAGE_COMPETITOR_COLUMN_MIN_MATCH`.
 * - `no_demand`: below `NEW_PAGE_MIN_RIVAL_VISITS`.
 *
 * `ownSegments` is null while the site's own URLs are unknown; the own-page
 * test is then skipped, and the review surface waits for them instead
 * (`supportingPagesSettling`).
 */
export function newPageIdeaBlocked(
  gap: { url: string; estimatedOrganicTraffic: number | null; focalMatchScore: number | null },
  ownSegments: ReadonlySet<string> | null
): NewPageIdeaBlock | null {
  const segment = finalPathSegment(gap.url);
  if (ownSegments && segment && ownSegments.has(segment)) return 'own_page_exists';
  if ((gap.focalMatchScore ?? 0) < SUPPORTING_PAGE_COMPETITOR_COLUMN_MIN_MATCH) return 'off_topic';
  if (Math.round(gap.estimatedOrganicTraffic ?? 0) < NEW_PAGE_MIN_RIVAL_VISITS) return 'no_demand';
  return null;
}

/** The final path segments of our own site's URLs, for `newPageIdeaBlocked`. */
export function ownPathSegments(ownSiteUrls: readonly string[] | null | undefined): ReadonlySet<string> | null {
  if (!ownSiteUrls) return null;
  const out = new Set<string>();
  for (const url of ownSiteUrls) {
    const seg = finalPathSegment(url);
    if (seg) out.add(seg);
  }
  return out;
}

export interface ReviewSupportingPagesGap {
  url: string;
  /** Pathname only — the domain is stated once, on the column. */
  path: string;
  /** Formatted by the host, which owns the existing traffic wording. */
  traffic: string | null;
  /** The rival page's own estimated monthly visits, unformatted. Null when unknown. */
  estimatedOrganicTraffic: number | null;
  /** How closely the rival page matches this page's topic (FW-URL-05). Null when unscored. */
  focalMatchScore: number | null;
}

export interface ReviewSupportingPagesColumn {
  domain: string;
  /** "productive.io has 2 pages on this topic you don't." */
  verdict: string;
  gaps: ReviewSupportingPagesGap[];
}

export interface ReviewSupportingPagesFormatters {
  path: (url: string) => string;
  traffic: (value?: number) => string | null;
}

/**
 * A competitor row is a gap when nothing on your site covers it.
 *
 * The same `similarOnYourSite` the deep-dive card paints green or red, read
 * here as a filter rather than a colour. That it is the same field is the point:
 * two surfaces disagreeing about which pages you are missing would be worse
 * than either of them being sparse.
 */
function isGap(row: DomainTopicUrlRow): boolean {
  return !row.similarOnYourSite;
}

function verdictFor(domain: string, count: number): string {
  return count === 1
    ? `${domain} has 1 page on this topic you don’t.`
    : `${domain} has ${count} pages on this topic you don’t.`;
}

/**
 * A new-page idea whose subject is already a section of THIS page (TECHNICAL_DEBT §0ce): a rival's
 * page on one term of a metric, beside our post on the metric, which defines that term under its
 * own heading. Only this page, the one the idea was found next to: a section on another page of ours
 * is not a page on the subject. Site-wide, the same test blocks 21 of 76 ManyRequests ideas and 8 of
 * 43 SlideSpeak ones, a graded-good pricing idea among them (three other posts have a pricing
 * section for that product); on this page alone it blocks 3 and 2, each our page's own subject or a
 * section of it (2026-10-19 data). Same test as the section card's.
 */
function ideaAlreadyOnPage(url: string, ownPassages: readonly OwnPagePassage[] | null | undefined): AlreadyOnPageQuote | null {
  if (!ownPassages?.length) return null;
  return alreadyOnPageQuote(newPageIdeaSubject(url), ownPassages, { pageTitle: true });
}

/** A rival page left off the card because this page already covers its subject. */
export interface ReviewNewPageIdeaOnPage {
  url: string;
  domain: string;
  /** The idea as the card would have named it: the rival page's slug, in words. */
  idea: string;
  quote: AlreadyOnPageQuote;
  /** The quote in one line, as the card and the export print it. */
  quoteLine: string;
}

function guardedGaps(
  col: SupportingPagesUnifiedColumn,
  format: ReviewSupportingPagesFormatters,
  ownSegments: ReadonlySet<string> | null
): ReviewSupportingPagesGap[] {
  return (col.supportingRows ?? []).filter(isGap).map((row) => ({
    url: row.url,
    path: format.path(row.url),
    traffic: format.traffic(row.estimatedOrganicTraffic),
    estimatedOrganicTraffic:
      typeof row.estimatedOrganicTraffic === 'number' && Number.isFinite(row.estimatedOrganicTraffic)
        ? row.estimatedOrganicTraffic
        : null,
    focalMatchScore:
      typeof row.focalMatchScore === 'number' && Number.isFinite(row.focalMatchScore)
        ? row.focalMatchScore
        : null
  })).filter((gap) => newPageIdeaBlocked(gap, ownSegments) === null);
}

export function reviewSupportingPagesColumns(
  columns: readonly SupportingPagesUnifiedColumn[] | null | undefined,
  format: ReviewSupportingPagesFormatters,
  /** Our site's URLs; null while unknown (the own-page guard is then skipped). */
  ownSiteUrls: readonly string[] | null = null,
  /** This page's stored text (`ownPagePassages`); omitted, ideas are not checked against it. */
  ownPassages: readonly OwnPagePassage[] | null = null
): ReviewSupportingPagesColumn[] {
  const out: ReviewSupportingPagesColumn[] = [];
  const ownSegments = ownPathSegments(ownSiteUrls);

  for (const col of columns ?? []) {
    if (!col || col.isYourSite) continue;

    const gaps = guardedGaps(col, format, ownSegments).filter((gap) => !ideaAlreadyOnPage(gap.url, ownPassages));

    // A column with nothing missing has nothing to say on this surface. On the
    // deep-dive card it still earns its place — it is evidence that you cover
    // the topic — but here it would be a heading over an empty list.
    if (!gaps.length) continue;

    out.push({ domain: col.domain, verdict: verdictFor(col.domain, gaps.length), gaps });
  }

  return out;
}

/** The ideas `reviewSupportingPagesColumns` leaves out because this page already covers them, with the quote. */
export function reviewNewPageIdeasAlreadyOnPage(
  columns: readonly SupportingPagesUnifiedColumn[] | null | undefined,
  ownSiteUrls: readonly string[] | null,
  ownPassages: readonly OwnPagePassage[] | null
): ReviewNewPageIdeaOnPage[] {
  const out: ReviewNewPageIdeaOnPage[] = [];
  const ownSegments = ownPathSegments(ownSiteUrls);
  const plain: ReviewSupportingPagesFormatters = { path: (u) => u, traffic: () => null };
  for (const col of columns ?? []) {
    if (!col || col.isYourSite) continue;
    for (const gap of guardedGaps(col, plain, ownSegments)) {
      const quote = ideaAlreadyOnPage(gap.url, ownPassages);
      if (quote) {
        out.push({
          url: gap.url,
          domain: col.domain,
          idea: finalPathSegment(gap.url).replace(/[-_]+/g, ' '),
          quote,
          quoteLine: alreadyOnPageQuoteLine(quote)
        });
      }
    }
  }
  return out;
}

/** Whether the review card has anything to say at all. */
export function hasReviewSupportingPagesGaps(
  columns: readonly ReviewSupportingPagesColumn[] | null | undefined
): boolean {
  return (columns ?? []).length > 0;
}
